import { altitudeToPressure } from './altitude.js';

/**
 * Render annotation lines for key meteorological levels, Skysight style.
 *
 * Skysight technique (decoded from docs/skysight-skewt-bundle.js): for each
 * level (PBL top, LCL, EL), draw a horizontal dashed gray line at that
 * pressure's y-position, BUT split the line in two segments with the text
 * label centered in the gap. The line spans from x(tMin) on the left to the
 * skewX of the relevant temperature at that pressure on the right.
 *
 * Source:
 *   const r=this.y(t), d=this.x(this.tMin),
 *         a=this.getTlcl(l,n), e=this.skewX(a,t), c=e-d,
 *         m = s ? "Condensation Layer" : "Cu Cloudbase",
 *         w = ref.append("text").style("font-size","10px").text(m),
 *         h = w.node().getBBox().width; w.remove();
 *         o = d + c/2;  // mid x
 *         x = o - h/2 - 3 - 2; b = o + h/2 + 3 + 2;
 *         left line:  x1=d, x2=x, y=r
 *         right line: x1=b, x2=e, y=r
 *         text at (o, r) text-anchor middle, dominant-baseline middle
 *
 * Label selection (LCL): "Condensation Layer" if LCL altitude > PBL top
 * altitude (the parcel's saturation level is above the boundary layer top —
 * cumulus actually forms by mass ascent past PBL); otherwise "Cu Cloudbase".
 *
 * @param {import('d3-selection').Selection} container
 * @param {import('../skewt/scales.js').Scales} scales
 * @param {{
 *   plcl: number,
 *   tlcl: number,
 *   pblh: number,
 *   el: {pressure}|null,
 *   lclAboveBL: boolean,
 * }} levels
 * @param {Array} data
 */
const DEFAULT_LABELS = {
    topOfThermals: 'Top of Thermals',
    condensationLayer: 'Condensation Layer',
    cuCloudbase: 'Cu Cloudbase',
    equilibriumLevel: 'Equilibrium Level',
};

export function drawAnnotationPillboxes(container, scales, levels, data, i18n = {}) {
    const labels = { ...DEFAULT_LABELS, ...i18n };
    container.selectAll('.skewt-ext-pillboxes').remove();
    if (!levels) return;

    const layer = container.append('g').attr('class', 'skewt-ext-pillboxes');
    const xLeft = scales.x(scales.tMin);

    const STROKE = 'rgba(128, 128, 128, 0.5)';
    const DASH = '5, 3';
    const FONT_SIZE = '10px';
    const TEXT_FILL = 'rgb(80, 80, 80)';
    const PAD_X = 3;       // padding either side of text inside gap
    const PAD_EXTRA = 2;   // additional separation between text and line ends

    /**
     * Draw a single split-line annotation.
     * @param {number} y - y pixel
     * @param {number} xEnd - end x for the line (the temp's skewed x at this pressure)
     * @param {string} label
     */
    const drawSplit = (y, xEnd, label) => {
        // Measure the label by inserting a temporary text node
        const probe = layer
            .append('text')
            .attr('class', 'skewt-ext-pillbox-probe')
            .style('font-size', FONT_SIZE)
            .style('font-weight', 'normal')
            .text(label);
        let textW;
        try {
            textW = probe.node().getBBox().width;
        } catch (_) {
            textW = label.length * 6;
        }
        probe.remove();

        const mid = xLeft + (xEnd - xLeft) / 2;
        const gapLeft = mid - textW / 2 - PAD_X - PAD_EXTRA;
        const gapRight = mid + textW / 2 + PAD_X + PAD_EXTRA;

        // Left segment
        layer
            .append('line')
            .attr('x1', xLeft)
            .attr('x2', gapLeft)
            .attr('y1', y)
            .attr('y2', y)
            .attr('stroke', STROKE)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', DASH)
            .attr('pointer-events', 'none');

        // Right segment
        layer
            .append('line')
            .attr('x1', gapRight)
            .attr('x2', xEnd)
            .attr('y1', y)
            .attr('y2', y)
            .attr('stroke', STROKE)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', DASH)
            .attr('pointer-events', 'none');

        // Centered label
        layer
            .append('text')
            .attr('x', mid)
            .attr('y', y)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-size', FONT_SIZE)
            .style('font-weight', 'normal')
            .style('fill', TEXT_FILL)
            .style('pointer-events', 'none')
            .text(label);
    };

    // Proximity check (Skysight rule): if PBL top and LCL are within 18px
    // vertically, skip the "Top of Thermals" line and draw only "Cu Cloudbase".
    // Source: bundle — `if (u !== undefined && e !== undefined && Math.abs(u-e) < 18)
    //   drawLCLLineFromPressure(calculatedLCL.pressure, false);`
    let pblTooCloseToLCL = false;
    if (
        Number.isFinite(levels.pblh) &&
        Number.isFinite(levels.plcl) &&
        data && data.length
    ) {
        const pblP = altitudeToPressure(levels.pblh, data);
        if (Number.isFinite(pblP)) {
            const yPbl = scales.y(pblP);
            const yLcl = scales.y(levels.plcl);
            if (Math.abs(yPbl - yLcl) < 18) pblTooCloseToLCL = true;
        }
    }

    // PBL top: end x = where the temperature trace meets PBL height. Use the
    // surface temp lifted dry-adiabatically to PBL pressure as an approximation
    // (matches the boundary-layer-top line Skysight draws).
    if (!pblTooCloseToLCL && Number.isFinite(levels.pblh) && data && data.length) {
        const pblPress = altitudeToPressure(levels.pblh, data);
        if (Number.isFinite(pblPress)) {
            // Surface temp lifted dry-adiabatically to PBL pressure
            const surface = data[0];
            const KELVIN = 273.15;
            const POISSON = 0.286;
            const tPbl =
                (surface.temp + KELVIN) *
                    Math.pow(pblPress / surface.press, POISSON) -
                KELVIN;
            const xEnd = scales.skewX(tPbl, pblPress);
            drawSplit(scales.y(pblPress), xEnd, labels.topOfThermals);
        }
    }

    // LCL: line ends at the LCL temperature's skewed x at LCL pressure.
    if (Number.isFinite(levels.plcl) && Number.isFinite(levels.tlcl)) {
        const xEnd = scales.skewX(levels.tlcl, levels.plcl);
        const label = levels.lclAboveBL ? labels.condensationLayer : labels.cuCloudbase;
        drawSplit(scales.y(levels.plcl), xEnd, label);
    }

    // EL: line ends at the EL temperature (where parcel intersects environment).
    // findEL returns { pressure, temperature } — accept either `temp` (older
    // tests) or `temperature` (current findEL output).
    //
    // Skysight gate (decoded from bundle): only render the EL annotation when
    // calculatedEL.pressure > 250 hPa — i.e. EL is BELOW ~10500m altitude.
    // For very high EL (low pressure, near chart top) the annotation just
    // clutters the upper stratosphere and is omitted.
    if (levels.el && Number.isFinite(levels.el.pressure) && levels.el.pressure > 250) {
        const elT = Number.isFinite(levels.el.temperature)
            ? levels.el.temperature
            : levels.el.temp;
        if (Number.isFinite(elT)) {
            const xEnd = scales.skewX(elT, levels.el.pressure);
            drawSplit(scales.y(levels.el.pressure), xEnd, labels.equilibriumLevel);
        }
    }
}
